/**
 * Extract subdomain information from window.location.hostname
 * 
 * Handles:
 * - localhost / localhost:port
 * - app.myapp.com (app subdomain = authenticated app)
 * - myapp.com (no subdomain = authenticated app)
 * - user-site.myapp.com (any other subdomain = public portfolio)
 * 
 * @returns {Object} Subdomain metadata
 *   - subdomain: string | null (e.g., "user-site", null if app domain)
 *   - isAppDomain: boolean (true if localhost or app.myapp.com or myapp.com)
 *   - isPublicDomain: boolean (true if *.myapp.com where * is not "app")
 */
export function getSubdomainInfo() {
    if (typeof window === 'undefined') {
        // Server-side rendering or no window object
        return {
            subdomain: null,
            isAppDomain: false,
            isPublicDomain: false,
        };
    }

    const hostname = window.location.hostname.toLowerCase().replace('www.', '');

    // Development: localhost
    if (hostname === 'localhost' || hostname.startsWith('localhost:')) {
        return {
            subdomain: null,
            isAppDomain: true,
            isPublicDomain: false,
        };
    }

    // Split by dot to check subdomain structure
    const parts = hostname.split('.');

    // Single part (e.g., "localhost") - already handled above
    if (parts.length === 1) {
        return {
            subdomain: null,
            isAppDomain: true,
            isPublicDomain: false,
        };
    }

    // Two parts: either "myapp.com", "something.localhost", or "app.localhost"
    if (parts.length === 2) {
        // Special case: *.localhost is treated as a public subdomain (e.g., test.localhost → public)
        // But plain "localhost" is already handled above, so this catches "something.localhost"
        if (parts[1] === 'localhost') {
            const subdomain = parts[0];
            // "app.localhost" is reserved for authenticated app
            if (subdomain === 'app') {
                return {
                    subdomain: null,
                    isAppDomain: true,
                    isPublicDomain: false,
                };
            }
            // Any other *.localhost (e.g., test.localhost) is a public subdomain
            return {
                subdomain,
                isAppDomain: false,
                isPublicDomain: true,
            };
        }
        // Production 2-part domains (e.g., "myapp.com") are always app domain
        return {
            subdomain: null,
            isAppDomain: true,
            isPublicDomain: false,
        };
    }

    // Three or more parts: "subdomain.myapp.com" or "sub.subdomain.myapp.com"
    const subdomain = parts[0];

    // Check if subdomain is "app" (reserved for authenticated app)
    if (subdomain === 'app') {
        return {
            subdomain: null,
            isAppDomain: true,
            isPublicDomain: false,
        };
    }

    // Any other subdomain is a public portfolio
    return {
        subdomain,
        isAppDomain: false,
        isPublicDomain: true,
    };
}
